import { DisplayTheme, DisplayThemeValueSet } from '../../types';
import { BASE_COLORS, BOOKWISE_U_ALPHA, BOOKWISE_U_ALPHA_DARK, U_ALPHA_DARK, U_ALPHA_LIGHT } from './baseColors';

export type FunctionalThemeColors = {
  bgBlurred: DisplayThemeValueSet;
  bgCanvas: DisplayThemeValueSet;
  bgInteractive: DisplayThemeValueSet;
  bgInteractiveMild: DisplayThemeValueSet;
  backgroundPrimary: DisplayThemeValueSet;
  backgroundSecondary: DisplayThemeValueSet;
  backgroundTertiary: DisplayThemeValueSet;
  backgroundQuinary: DisplayThemeValueSet;
  backgroundElevated: DisplayThemeValueSet;
  backgroundBackdrop: DisplayThemeValueSet;
  backgroundMild: DisplayThemeValueSet;
  backgroundTextInput: DisplayThemeValueSet;

  borderPrimary: DisplayThemeValueSet;
  borderSecondary: DisplayThemeValueSet;
  borderSecondaryAlpha: DisplayThemeValueSet;
  borderTertiaryAlpha: DisplayThemeValueSet;

  buttonNeutralSubtleBackground: DisplayThemeValueSet;
  buttonNeutralSubtleIcon: DisplayThemeValueSet;
  buttonAccentSubtleBackground: DisplayThemeValueSet;
  buttonAccentSubtleColor: DisplayThemeValueSet;
  buttonAccentStrongBackground: DisplayThemeValueSet;
  buttonAccentStrongBackgroundDisabled: DisplayThemeValueSet;
  buttonAccentStrongIcon: DisplayThemeValueSet;
  buttonAccentStrongText: DisplayThemeValueSet;
  buttonCriticalSubtleBackground: DisplayThemeValueSet;

  iconDanger: DisplayThemeValueSet;
  iconFaint: DisplayThemeValueSet;
  iconPlaceholder: DisplayThemeValueSet;
  iconPrimary: DisplayThemeValueSet;
  iconSecondary: DisplayThemeValueSet;
  iconTertiary: DisplayThemeValueSet;
  iconInteractive: DisplayThemeValueSet;
  iconDisabled: DisplayThemeValueSet;
  iconInverse: DisplayThemeValueSet;

  text: DisplayThemeValueSet;
  textInverse: DisplayThemeValueSet;
  secondaryText: DisplayThemeValueSet;
  textTertiary: DisplayThemeValueSet;
  textInteractive: DisplayThemeValueSet;
  textError: DisplayThemeValueSet;
  textPlaceholder: DisplayThemeValueSet;
  textDisabled: DisplayThemeValueSet;
};

export const FUNCTIONAL_COLORS: FunctionalThemeColors = {
  text: {
    [DisplayTheme.Light]: BASE_COLORS.neutral20,
    [DisplayTheme.Dark]: BASE_COLORS.neutral95,
    highContrast: {
      [DisplayTheme.Light]: 'black',
      [DisplayTheme.Dark]: 'white',
    },
    bookwise: {
      [DisplayTheme.Light]: BASE_COLORS.stone20,
      [DisplayTheme.Dark]: BASE_COLORS.stone90,
    },
  },
  secondaryText: {
    [DisplayTheme.Light]: BASE_COLORS.neutral45,
    [DisplayTheme.Dark]: BASE_COLORS.neutral65,
    bookwise: {
      [DisplayTheme.Light]: BASE_COLORS.stone50,
      [DisplayTheme.Dark]: BASE_COLORS.stone55,
    },
  },
  textTertiary: {
    [DisplayTheme.Light]: BASE_COLORS.neutral60,
    [DisplayTheme.Dark]: BASE_COLORS.neutral50,
    bookwise: {
      [DisplayTheme.Light]: BASE_COLORS.stone65,
      [DisplayTheme.Dark]: BASE_COLORS.stone45,
    },
  },
  textInverse: {
    [DisplayTheme.Light]: BASE_COLORS.neutral100,
    [DisplayTheme.Dark]: BASE_COLORS.neutral20,
    bookwise: {
      [DisplayTheme.Light]: BASE_COLORS.stone90,
      [DisplayTheme.Dark]: BASE_COLORS.stone20,
    },
  },
  textInteractive: {
    [DisplayTheme.Light]: BASE_COLORS.blue50,
    [DisplayTheme.Dark]: BASE_COLORS.blue70,
    highContrast: {
      [DisplayTheme.Light]: BASE_COLORS.blue20,
      [DisplayTheme.Dark]: BASE_COLORS.blue80,
    },
    bookwise: {
      [DisplayTheme.Light]: BASE_COLORS.blue50,
      [DisplayTheme.Dark]: BASE_COLORS.blue70,
    },
  },
  textError: {
    [DisplayTheme.Light]: BASE_COLORS.red50,
    [DisplayTheme.Dark]: BASE_COLORS.red60,
    bookwise: {
      [DisplayTheme.Light]: BASE_COLORS.red50,
      [DisplayTheme.Dark]: BASE_COLORS.red60,
    },
  },
  textPlaceholder: {
    [DisplayTheme.Light]: BASE_COLORS.neutral65,
    [DisplayTheme.Dark]: BASE_COLORS.neutral45,
    bookwise: {
      [DisplayTheme.Light]: BASE_COLORS.stone300,
      [DisplayTheme.Dark]: BASE_COLORS.stone45,
    },
  },
  textDisabled: {
    [DisplayTheme.Light]: BASE_COLORS.neutral75,
    [DisplayTheme.Dark]: BASE_COLORS.neutral40,
    highContrast: {
      [DisplayTheme.Light]: BASE_COLORS.neutral65,
      [DisplayTheme.Dark]: BASE_COLORS.neutral50,
    },
    bookwise: {
      [DisplayTheme.Light]: BASE_COLORS['stone65-50'],
      [DisplayTheme.Dark]: BASE_COLORS['stone65-50'],
    },
  },

  bgBlurred: {
    [DisplayTheme.Light]: BASE_COLORS.neutral80,
    [DisplayTheme.Dark]: '#000c15',
    bookwise: {
      [DisplayTheme.Light]: BASE_COLORS.stone97,
      [DisplayTheme.Dark]: BASE_COLORS.stone10,
    },
  },
  bgCanvas: {
    [DisplayTheme.Light]: BASE_COLORS.neutral95,
    [DisplayTheme.Dark]: BASE_COLORS.neutral00,
    bookwise: {
      [DisplayTheme.Light]: BASE_COLORS.sepia100,
      [DisplayTheme.Dark]: BASE_COLORS.stone10,
    },
  },
  bgInteractive: {
    [DisplayTheme.Light]: BASE_COLORS.blue50,
    [DisplayTheme.Dark]: BASE_COLORS.blue70,
    bookwise: {
      [DisplayTheme.Light]: BASE_COLORS.orange50,
      [DisplayTheme.Dark]: BASE_COLORS.orange50,
    },
  },
  bgInteractiveMild: {
    [DisplayTheme.Light]: BASE_COLORS.orange90,
    [DisplayTheme.Dark]: `${BASE_COLORS.orange50}20`,
  },
  backgroundPrimary: {
    [DisplayTheme.Light]: BASE_COLORS.neutral100,
    [DisplayTheme.Dark]: BASE_COLORS.neutral05,
    bookwise: {
      [DisplayTheme.Light]: BASE_COLORS.stone97,
      [DisplayTheme.Dark]: BASE_COLORS.stone10,
    },
  },
  backgroundSecondary: {
    [DisplayTheme.Light]: BASE_COLORS.neutral97,
    [DisplayTheme.Dark]: BASE_COLORS.neutral10,
    bookwise: {
      [DisplayTheme.Light]: BASE_COLORS.stone95,
      [DisplayTheme.Dark]: BASE_COLORS.stone20,
    },
  },
  backgroundTertiary: {
    [DisplayTheme.Light]: BASE_COLORS.neutral95,
    [DisplayTheme.Dark]: BASE_COLORS.neutral15,
    bookwise: {
      [DisplayTheme.Light]: BASE_COLORS.stone93,
      [DisplayTheme.Dark]: BASE_COLORS.stone15,
    },
  },
  backgroundQuinary: {
    [DisplayTheme.Light]: BASE_COLORS.neutral97,
    [DisplayTheme.Dark]: BASE_COLORS.neutral15,
    bookwise: { // TODO
      [DisplayTheme.Light]: BASE_COLORS.neutral97,
      [DisplayTheme.Dark]: BASE_COLORS.neutral15,
    },
  },
  backgroundElevated: {
    [DisplayTheme.Light]: BASE_COLORS.neutral100,
    [DisplayTheme.Dark]: BASE_COLORS.neutral10,
    bookwise: {
      [DisplayTheme.Light]: BASE_COLORS.stone100,
      [DisplayTheme.Dark]: BASE_COLORS.stone15,
    },
  },
  backgroundBackdrop: {
    [DisplayTheme.Light]: BASE_COLORS.black,
    [DisplayTheme.Dark]: BASE_COLORS.black,
  },
  backgroundMild: {
    [DisplayTheme.Light]: BASE_COLORS.stone93,
    [DisplayTheme.Dark]: BASE_COLORS.neutral15,
  },
  backgroundTextInput: {
    [DisplayTheme.Light]: BASE_COLORS.neutral100,
    [DisplayTheme.Dark]: 'rgba(110, 120, 130, 0.12)',
  },
  borderPrimary: {
    [DisplayTheme.Light]: BASE_COLORS.neutral80,
    [DisplayTheme.Dark]: BASE_COLORS.neutral20,
    highContrast: {
      [DisplayTheme.Light]: BASE_COLORS.neutral20,
      [DisplayTheme.Dark]: BASE_COLORS.neutral90,
    },
    bookwise: {
      [DisplayTheme.Light]: BASE_COLORS.stone85,
      [DisplayTheme.Dark]: BASE_COLORS.stone40,
    },
  },
  borderSecondary: {
    [DisplayTheme.Light]: BASE_COLORS.neutral90,
    [DisplayTheme.Dark]: BASE_COLORS.neutral15,
    highContrast: {
      [DisplayTheme.Light]: BASE_COLORS.neutral35,
      [DisplayTheme.Dark]: BASE_COLORS.neutral80,
    },
    bookwise: {
      [DisplayTheme.Light]: BASE_COLORS.stone93,
      [DisplayTheme.Dark]: BASE_COLORS.stone15,
    },
  },
  borderSecondaryAlpha: {
    [DisplayTheme.Light]: U_ALPHA_LIGHT.uAlpha300,
    [DisplayTheme.Dark]: U_ALPHA_DARK.uAlpha300,
    highContrast: {
      [DisplayTheme.Light]: BASE_COLORS.neutral40,
      [DisplayTheme.Dark]: BASE_COLORS.neutral70,
    },
    bookwise: {
      [DisplayTheme.Light]: BOOKWISE_U_ALPHA.uAlpha300, // 300 alpha
      [DisplayTheme.Dark]: BOOKWISE_U_ALPHA_DARK.uAlpha300, // 300 alpha
    },
  },
  borderTertiaryAlpha: {
    [DisplayTheme.Light]: U_ALPHA_LIGHT.uAlpha200,
    [DisplayTheme.Dark]: U_ALPHA_DARK.uAlpha200,
    highContrast: {
      [DisplayTheme.Light]: BASE_COLORS.neutral45,
      [DisplayTheme.Dark]: BASE_COLORS.neutral70,
    },
  },
  buttonNeutralSubtleBackground: {
    [DisplayTheme.Light]: 'rgba(110, 120, 130, 0.15)',
    [DisplayTheme.Dark]: 'rgba(110, 120, 130, 0.2)',
  },
  buttonNeutralSubtleIcon: {
    [DisplayTheme.Light]: 'rgba(96, 108, 121, 1)',
    [DisplayTheme.Dark]: 'rgba(147, 159, 173, 1)',
  },
  buttonAccentStrongBackground: {
    [DisplayTheme.Light]: BASE_COLORS.blue50,
    [DisplayTheme.Dark]: BASE_COLORS.blue70,
  },
  buttonAccentStrongBackgroundDisabled: {
    [DisplayTheme.Light]: 'rgba(110, 120, 130, 0.1)',
    [DisplayTheme.Dark]: 'rgba(110, 120, 130, 0.2)',
  },
  buttonAccentStrongIcon: {
    [DisplayTheme.Light]: BASE_COLORS.neutral100,
    [DisplayTheme.Dark]: BASE_COLORS.neutral00,
  },
  buttonAccentStrongText: {
    [DisplayTheme.Light]: BASE_COLORS.neutral100,
    [DisplayTheme.Dark]: BASE_COLORS.neutral00,
  },
  buttonAccentSubtleBackground: {
    [DisplayTheme.Light]: 'rgba(45, 117, 229, 0.10)',
    [DisplayTheme.Dark]: 'rgba(45, 117, 229, 0.23)',
  },
  buttonAccentSubtleColor: {
    [DisplayTheme.Light]: '#2675DE',
    [DisplayTheme.Dark]: '#7CAEEC',
  },
  buttonCriticalSubtleBackground: {
    [DisplayTheme.Light]: 'rgba(216, 64, 58, 0.1)',
    [DisplayTheme.Dark]: 'rgba(216, 64, 58, 0.23)',
  },

  iconDanger: {
    [DisplayTheme.Light]: BASE_COLORS.red50,
    [DisplayTheme.Dark]: BASE_COLORS.red50,
    bookwise: {
      [DisplayTheme.Light]: BASE_COLORS.red50,
      [DisplayTheme.Dark]: BASE_COLORS.red60,
    },
  },
  iconFaint: {
    [DisplayTheme.Light]: U_ALPHA_LIGHT.uAlpha200,
    [DisplayTheme.Dark]: BASE_COLORS.neutral30,
  },
  iconPlaceholder: {
    [DisplayTheme.Light]: '#939FAD',
    [DisplayTheme.Dark]: '#939FAD',
  },
  iconPrimary: {
    [DisplayTheme.Light]: BASE_COLORS.neutral20,
    [DisplayTheme.Dark]: BASE_COLORS.neutral95,
    bookwise: {
      [DisplayTheme.Light]: BASE_COLORS.stone20,
      [DisplayTheme.Dark]: BASE_COLORS.stone90,
    },
  },
  iconSecondary: {
    [DisplayTheme.Light]: BASE_COLORS.neutral45,
    [DisplayTheme.Dark]: BASE_COLORS.neutral70,
    bookwise: {
      [DisplayTheme.Light]: BASE_COLORS.stone50,
      [DisplayTheme.Dark]: BASE_COLORS.stone50,
    },
  },
  iconTertiary: {
    [DisplayTheme.Light]: BASE_COLORS.neutral60,
    [DisplayTheme.Dark]: BASE_COLORS.neutral45,
    bookwise: {
      [DisplayTheme.Light]: BASE_COLORS.stone75,
      [DisplayTheme.Dark]: BASE_COLORS.stone25,
    },
  },
  iconInteractive: {
    [DisplayTheme.Light]: BASE_COLORS.blue50,
    [DisplayTheme.Dark]: BASE_COLORS.blue70,
    highContrast: {
      [DisplayTheme.Light]: BASE_COLORS.blue20,
      [DisplayTheme.Dark]: BASE_COLORS.blue80,
    },
    bookwise: {
      [DisplayTheme.Light]: BASE_COLORS.orange50,
      [DisplayTheme.Dark]: BASE_COLORS.orange70,
    },
    bookwiseHighContrast: {
      [DisplayTheme.Light]: BASE_COLORS.orange50,
      [DisplayTheme.Dark]: BASE_COLORS.orange70,
    },
  },
  iconDisabled: {
    [DisplayTheme.Light]: BASE_COLORS.neutral70,
    [DisplayTheme.Dark]: BASE_COLORS.neutral40,
    highContrast: {
      [DisplayTheme.Light]: BASE_COLORS.neutral60,
      [DisplayTheme.Dark]: BASE_COLORS.neutral50,
    },
    bookwise: {
      [DisplayTheme.Light]: BASE_COLORS['stone20-85'],
      [DisplayTheme.Dark]: BASE_COLORS['stone90-85'],
    },
    bookwiseHighContrast: {
      [DisplayTheme.Light]: BASE_COLORS.stone20,
      [DisplayTheme.Dark]: BASE_COLORS.stone90,
    },
  },
  iconInverse: {
    [DisplayTheme.Light]: BASE_COLORS.neutral97,
    [DisplayTheme.Dark]: BASE_COLORS.neutral20,
    bookwise: {
      [DisplayTheme.Light]: BASE_COLORS.stone90,
      [DisplayTheme.Dark]: BASE_COLORS.stone20,
    },
  },
};
